import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';

import { AfregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/afregning-dto';
import { GetPriceServiceaftaleResponseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/get-price-serviceaftale-response-dto';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { TilvalgGroupDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-group-dto';
import { ServiceAftaleService, TilvalgService, AfregningService, PriceService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { UpdateTilvalgOnServiceaftaleRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-tilvalg-on-serviceaftale-request';
import { GetPriceRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-price-request';
import { fadeSlideInOut, inOutAnimation } from 'src/app/common/animations';
import { LogicService } from 'src/app/services/logic.service';

@Component({
    selector: 'app-edit-tilvalg',
    templateUrl: './edit-tilvalg.component.html',
    styleUrls: ['./edit-tilvalg.component.scss'],
    animations: [fadeSlideInOut, inOutAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditTilvalgComponent implements OnInit {
  @ViewChild('stepper', { static: false }) public flowStepper: MatStepper;

  stepperIndex: number = 0;
  quickViewOpen: boolean = false;

  serviceaftaleNo: number;
  choice: number;

  tilvalgForm: UntypedFormGroup;
  opkraevningForm: UntypedFormGroup;
  sendLetterForm: UntypedFormGroup;

  serviceaftale: ServiceaftaleDto;
  afregninger: AfregningDto[] = [];

  tilvalgGroups: TilvalgGroupDto[];

  currentPrice: GetPriceServiceaftaleResponseDto;
  newPrice: GetPriceServiceaftaleResponseDto;

  validFrom: Date;
  upcomingChange: boolean = false;

  afregningerTotalMedMoms: number;
  afregningerTotalExclMoms: number;

  opkraevningerTotalMedMoms: number;
  opkraevningerTotalExclMoms: number;

  priceError: string;

  isGarantiNaerVarmeLeasingGasTryghed: boolean = false;

  data$: Observable<any>;

  afregninger$: Observable<any>;
  unsubscribe$ = new Subject<void>();

  get tilvalg() {
    return this.tilvalgForm.get('tilvalgGroups');
  }

  get opkraevningType() {
    return this.opkraevningForm.get('opkraevningType');
  }

  get sendLetter() {
    return this.sendLetterForm.get('sendLetter');
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private serviceaftaleService: ServiceAftaleService,
    private priceService: PriceService,
    private tilvalgService: TilvalgService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private afregningService: AfregningService,
    private logic: LogicService
  ) {
    this.validFrom = new Date(this.route.snapshot.queryParams.validFrom);

    this.tilvalgForm = this.formBuilder.group({
      tilvalgGroups: this.formBuilder.array([]),
    });
    this.opkraevningForm = this.formBuilder.group({
      opkraevningType: [null, [Validators.required]],
    });
    this.sendLetterForm = this.formBuilder.group({
      sendLetter: [true, Validators.required],
    });
  }

  ngOnInit(): void {
    this.serviceaftaleNo = Number(this.route.snapshot.paramMap.get('serviceaftaleNo'));
    this.data$ = defer(() => {
      this.loader.show();
      return this.serviceaftaleService.getServiceaftaleByNo({ caller: EGIS_SERVICE_CALLER, serviceaftaleNo: this.serviceaftaleNo }).pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
        catchError((err) => {
          this.errorHandler.HandleError(err);
          return of(null);
        }),
        tap((serviceaftale: ServiceaftaleDto) => {
          this.serviceaftale = serviceaftale;

          this.isGarantiNaerVarmeLeasingGasTryghed =
            serviceaftale.kunde?.kundeExtendedInformation?.harNaervarmeAftale ||
            serviceaftale.kunde?.kundeExtendedInformation?.harLeasingAftale ||
            serviceaftale.kunde?.kundeExtendedInformation?.harGasfyrTryghedAftale;

          this.sendLetter.setValue(!this.isGarantiNaerVarmeLeasingGasTryghed);

          this.upcomingChange =
            new Date(serviceaftale.startDate) > new Date()
              ? this.validFrom > new Date(serviceaftale.startDate)
              : this.validFrom > new Date();

          this.getCurrentPrice();
        }),
        flatMap((serviceaftale: ServiceaftaleDto) =>
          forkJoin([
            this.afregningService
              .getCustomerOpkraevningerForCurrentServiceinterval({
                serviceaftaleId: serviceaftale.id,
                caller: EGIS_SERVICE_CALLER,
              })
              .pipe(
                tap((getAfregningerForServiceaftaleResponse) => {
                  this.afregninger = getAfregningerForServiceaftaleResponse.filter(
                    (p) => !p.hasKreditering && !p.kreditnotaForServiceafregningId
                  );
                  this.afregningerTotalMedMoms = this.afregninger.reduce((sum, current) => sum + current.beloebMedMoms, 0);
                  this.afregningerTotalExclMoms = this.afregninger.reduce((sum, current) => sum + current.beloebExclMoms, 0);
                })
              ),
            this.tilvalgService
              .getTilvalgGroupsForServiceabonnement({
                serviceAbonnementId: serviceaftale.serviceAbonnement.id,
                caller: EGIS_SERVICE_CALLER,
              })
              .pipe(
                tap((getTilvalgGroupsResponse: TilvalgGroupDto[]) => {
                  this.tilvalgGroups = getTilvalgGroupsResponse;
                  const control = <UntypedFormArray>this.tilvalgForm.controls['tilvalgGroups'];
                  control.clear();
                  getTilvalgGroupsResponse.forEach((x) => {
                    control.push(new UntypedFormControl(this.findValueForControl(x), [Validators.required]));
                  });

                  this.tilvalgForm.valueChanges
                    .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
                    .subscribe((tilvalgInformation) => {
                      this.getNewPrice();
                    });
                })
              ),
          ])
        )
      );
    });
  }

  private findValueForControl(tilvalgGroup: TilvalgGroupDto) {
    let value = null;
    this.serviceaftale.tilvalg.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x.tilvalgnr == y.tilvalgnr) {
          value = x.tilvalgnr;
        }
      });
    });
    return value;
  }

  onCloseClick() {
    if (!this.tilvalgForm.dirty) {
      this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
    } else {
      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Er du sikker?',
          body: 'Er du sikker på at du vil afbryde?',
        },
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
        }
      });
    }
  }

  doChange(stepper: MatStepper) {
    let tilvalgnrList = this.tilvalg.value.filter((x) => x != null && Number.isSafeInteger(x));

    const request: UpdateTilvalgOnServiceaftaleRequest = {
      serviceaftaleId: this.serviceaftale.id,
      tilvalgnrList: tilvalgnrList,
      validFrom: this.validFrom.toOkDateString(),
      krediter: this.opkraevningType.value === 1 ? true : null,
      sendLetter: this.sendLetter.value,
      fitServiceInterval: !this.upcomingChange,
    };

    this.loader.show();

    this.tilvalgService
      .updateTilvalgOnServiceaftale({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        () => {
          stepper.next();
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  getCurrentPrice() {
    this.priceService
      .getPriceForServiceAftale({
        caller: EGIS_SERVICE_CALLER,
        serviceaftaleId: this.serviceaftale.id,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (price: GetPriceServiceaftaleResponseDto) => {
          this.currentPrice = price;
        },
        (error: HttpErrorResponse) => {
          this.handlePriceError(error);
        }
      );
  }

  getNewPrice() {
    let tilvalgnrList = this.tilvalg.value.filter((x) => x != null && Number.isSafeInteger(x));
    const request: GetPriceRequest = {
      serviceaftaleId: this.serviceaftale.id,
      serviceabonnementId: this.serviceaftale.serviceAbonnement.id,
      tilvalgnrList: tilvalgnrList,
      validFrom: this.validFrom.toOkDateString(),
      naesteEftersynDate: this.serviceaftale.serviceAbonnement.isTilkaldsabonnement ? null : this.serviceaftale.naesteEftersynDate,
      opkraevningDate: this.serviceaftale.serviceAbonnement.isTilkaldsabonnement ? null : this.serviceaftale.opkraevningDate,
      opkraevningsinterval: this.serviceaftale.serviceAbonnement.isTilkaldsabonnement ? null : this.serviceaftale.opkraevningsinterval,
    };

    this.priceService
      .getPriceForEditedServiceAftale({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (price: GetPriceServiceaftaleResponseDto) => {
          this.newPrice = price;
          this.opkraevningerTotalMedMoms = this.afregninger.length * price.opkraevning.amount.withMoms;
          this.opkraevningerTotalExclMoms = this.afregninger.length * price.opkraevning.amount.withoutMoms;
        },
        (error: HttpErrorResponse) => {
          this.handlePriceError(error);
        }
      );
  }

  handlePriceError(error: HttpErrorResponse) {
    if (error && error.status === 400) {
      const errorText = this.errorHandler.getValidationErrorText(error);
      this.priceError = errorText;
    }
  }

  getCurrentTilvalg(tilvalgGroup: TilvalgGroupDto): string {
    let value = null;
    this.serviceaftale.tilvalg.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x.tilvalgnr == y.tilvalgnr) {
          value = x.name;
        }
      });
    });
    return value;
  }

  getNewTilvalg(tilvalgGroup: TilvalgGroupDto) {
    const t = this.getCurrentTilvalg(tilvalgGroup);
    let value = null;
    this.tilvalg.value.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x == y.tilvalgnr) {
          value = y.name;
        }
      });
    });
    return value !== t ? value : null;
  }

  getProductText(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  reloadServiceAftale() {
    this.loader.show();
    this.serviceaftaleService
      .getServiceaftaleByNo({ caller: EGIS_SERVICE_CALLER, serviceaftaleNo: this.serviceaftaleNo })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (serviceaftale: ServiceaftaleDto) => {
          this.serviceaftale = serviceaftale;
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  goBack() {
    if (this.flowStepper.selectedIndex != 0) {
      this.flowStepper.previous();
    }
  }

  goToStep(stepNumber: number) {
    this.flowStepper.selectedIndex = stepNumber;
  }

  goToSubscription() {
    this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
  }
}
